import { useEffect, useState } from "react";
import { Columns3, LayoutDashboard, List, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
	SearchableSelect,
	type SearchableSelectOption,
} from "@/components/ui/searchable-select";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { PublicFilterOptions } from "@/services/public-view.service";
import {
	ALL_SPRINTS,
	type PublicFilterKey,
	type PublicFilterValues,
	type PublicView,
} from "./filters";

const PRIORITY_OPTIONS: SearchableSelectOption[] = [
	{ value: "urgent", label: "Urgent" },
	{ value: "high", label: "High" },
	{ value: "medium", label: "Medium" },
	{ value: "low", label: "Low" },
];

function withAll(label: string, options: SearchableSelectOption[], allValue = "") {
	return [{ value: allValue, label }, ...options];
}

const VIEW_BUTTONS: { view: PublicView; label: string; icon: typeof List }[] = [
	{ view: "roadmap", label: "Overview", icon: LayoutDashboard },
	{ view: "board", label: "Column view", icon: Columns3 },
	{ view: "list", label: "List view", icon: List },
];

export function PublicTaskFilters({
	values,
	options,
	view,
	isFiltered,
	onChange,
	onClear,
	onViewChange,
}: {
	values: PublicFilterValues;
	options?: PublicFilterOptions;
	view: PublicView;
	isFiltered: boolean;
	onChange: (key: PublicFilterKey, value: string) => void;
	onClear: () => void;
	onViewChange: (view: PublicView) => void;
}) {
	const [search, setSearch] = useState(values.q);
	const [syncedQ, setSyncedQ] = useState(values.q);

	// Keep the box in sync when the URL changes elsewhere (Clear, back button).
	if (values.q !== syncedQ) {
		setSyncedQ(values.q);
		setSearch(values.q);
	}

	useEffect(() => {
		if (search === values.q) return;
		const id = setTimeout(() => onChange("q", search), 300);
		return () => clearTimeout(id);
	}, [search, values.q, onChange]);

	const loading = !options;

	const selects: {
		key: Exclude<PublicFilterKey, "q">;
		label: string;
		width: string;
		allValue?: string;
		options: SearchableSelectOption[];
	}[] = [
		{
			key: "project",
			label: "All Projects",
			width: "sm:w-48",
			options: (options?.projects ?? []).map((p) => ({ value: p.id, label: p.name })),
		},
		{
			key: "sprint",
			label: "All Sprints",
			width: "sm:w-44",
			allValue: ALL_SPRINTS,
			options: (options?.sprints ?? []).map((s) => ({ value: s.id, label: s.name })),
		},
		{
			key: "status",
			label: "All Statuses",
			width: "sm:w-40",
			options: (options?.stages ?? []).map((s) => ({ value: s.key, label: s.name })),
		},
		{ key: "priority", label: "All Priorities", width: "sm:w-36", options: PRIORITY_OPTIONS },
		{
			key: "assignee",
			label: "All Assignees",
			width: "sm:w-44",
			options: (options?.assignees ?? []).map((a) => ({
				value: a.id,
				label: a.full_name ?? "Unnamed",
			})),
		},
	];

	return (
		<div className="mb-6 flex flex-wrap items-center gap-3">
			<div className="relative w-full sm:w-56">
				<Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
				<Input
					placeholder="Search tasks..."
					aria-label="Search tasks"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					className="h-9 w-full pl-8 text-sm"
				/>
			</div>

			{/* Columns already group by status, so the status filter is redundant there. */}
			{selects
				.filter((s) => view === "list" || s.key !== "status")
				.map((s) => (
					<div key={s.key} className={`w-full ${s.width}`}>
						<SearchableSelect
							ariaLabel={s.label}
							value={values[s.key]}
							onValueChange={(v) => onChange(s.key, v)}
							placeholder={s.label}
							options={withAll(s.label, s.options, s.allValue)}
							disabled={loading && s.key !== "priority"}
							loading={loading && s.key !== "priority"}
						/>
					</div>
				))}

			{isFiltered && (
				<button
					type="button"
					onClick={onClear}
					className="text-xs text-muted hover:text-foreground"
				>
					Clear filters
				</button>
			)}

			<div className="ml-auto flex items-center overflow-hidden rounded-lg border border-border bg-surface">
				{VIEW_BUTTONS.map(({ view: v, label, icon: ViewIcon }) => (
					<Tooltip key={v}>
						<TooltipTrigger asChild>
							<button
								type="button"
								aria-label={label}
								aria-pressed={view === v}
								onClick={() => onViewChange(v)}
								className={cn(
									"p-2 transition-colors",
									view === v
										? "bg-primary text-primary-foreground"
										: "text-muted hover:bg-muted-subtle hover:text-foreground",
								)}
							>
								<ViewIcon className="h-4 w-4" />
							</button>
						</TooltipTrigger>
						<TooltipContent side="bottom">{label}</TooltipContent>
					</Tooltip>
				))}
			</div>
		</div>
	);
}
